import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import BaseTable, { Column } from 'react-base-table';
import { TableWrapper, HeaderRow, HeaderTitle, FullCell, Cell } from './styled';
import { SortableRow } from './SortableRow';
import useNetworkMetricsData from '../hooks/useNetworkMetricsData';
import Retry from '@src/components/common/Retry';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import useDndUtils from '../hooks/useDndUtils';
import useNetworkOverview from '../../hooks/useNetworkOverview';

interface RowRendererProps {
  rowData: { metricKey: string };
  data: any;
  dodData: any;
}

const RowRenderer = ({ rowData, data, dodData }: RowRendererProps) => (
  <SortableRow metricKey={rowData.metricKey} data={data} dodData={dodData} />
);

export default function NetworkTable() {
  const [width, setWidth] = useState(800);
  const containerRef = useRef(null);
  const query = useNetworkMetricsData();
  const { networkMetrics: orderedMetrics } = useNetworkOverview();

  const rows = useMemo(
    () => orderedMetrics.map((key) => ({ id: key, metricKey: key })),
    [orderedMetrics],
  );

  const { handleDragEnd } = useDndUtils(orderedMetrics);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const rowHeight = 64;
  const height = rows.length * rowHeight;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        setWidth(entries[0].contentRect.width);
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const rowRenderer = useCallback(
    ({ rowData }) => <RowRenderer rowData={rowData} data={query.data} dodData={query.dod?.data} />,
    [query.data, query.dod?.data],
  );

  return (
    <TableWrapper ref={containerRef} className="network-table-container">
      {/* Manual header — same flex slots as RowComponent, titles sit at the same padding edge */}
      <HeaderRow>
        {/* 32px spacer mirrors the RowWrapper padding-left so flex widths align */}
        <div style={{ width: 32, flexShrink: 0 }} />
        <FullCell as="div">
          <HeaderTitle>Metric</HeaderTitle>
        </FullCell>
        <Cell as="div">
          <HeaderTitle>Count</HeaderTitle>
        </Cell>
        <Cell as="div" $flex={2}>
          <HeaderTitle>Percentage</HeaderTitle>
        </Cell>
      </HeaderRow>

      <Retry
        progress
        isLoading={query.isFetching}
        showError={query.isError}
        fetchData={query.refetch}
        queued={false}
        horizontal={false}
        compact={false}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedMetrics} strategy={verticalListSortingStrategy}>
            <BaseTable
              data={rows}
              rowKey="id"
              width={width}
              height={height}
              rowHeight={rowHeight}
              headerHeight={0}
              rowClassName={() => 'network-row'}
              rowRenderer={rowRenderer}
              overscanColumnCount={1}
            >
              <Column key="name" dataKey="name" width={width} />
            </BaseTable>
          </SortableContext>
        </DndContext>
      </Retry>
    </TableWrapper>
  );
}
